import * as XLSX from 'xlsx';

export const AFFILIATE_KEYS = [
  'kakobuy', 'oopbuy', 'lovegobuy', 'superbuy', 'mulebuy', 'joyagoo',
  'cnfans', 'eastmallbuy', 'orientdig', 'loongbuy',
  'basetao', 'itaobuy', 'cssbuy', 'sugargoo', 'hoobuy',
  'ponybuy', 'hipobuy', 'litbuy', 'gtbuy', 'cnshopper', 'mycnbox',
  'bbdbuy', 'fansbuy', 'pingubuy', 'fishgoo',
] as const;

export type OperationsIssue =
  | 'missing_name'
  | 'missing_brand'
  | 'missing_link'
  | 'missing_price'
  | 'missing_category'
  | 'missing_image'
  | 'invalid_price'
  | 'duplicate_name'
  | 'duplicate_link'
  | 'duplicate_order'
  | 'missing_affiliates';

export interface OperationsProduct {
  id: string;
  name?: string | null;
  brand?: string | null;
  price?: number | string | null;
  image?: string | null;
  link?: string | null;
  category?: string | null;
  order?: number | null;
  [key: string]: unknown;
}

export interface OperationsRow extends OperationsProduct {
  issues: OperationsIssue[];
}

export interface OperationsFilters {
  query?: string;
  brand?: string;
  category?: string;
  minPrice?: number | null;
  maxPrice?: number | null;
  issue?: OperationsIssue | 'all';
}

const ISSUE_TYPES: OperationsIssue[] = [
  'missing_name', 'missing_brand', 'missing_link', 'missing_price',
  'missing_category', 'missing_image', 'invalid_price', 'duplicate_name',
  'duplicate_link', 'duplicate_order', 'missing_affiliates',
];

const OMITTED_EXPORT_KEYS = new Set(['id', 'search_aliases']);

const PREFERRED_EXPORT_KEYS = [
  'name', 'brand', 'category', 'price', 'order', 'image', 'link', 'batch',
  'created_by', 'created_at', 'updated_at', ...AFFILIATE_KEYS,
];

function normalizedText(value: unknown) {
  return String(value ?? '').trim().toLowerCase();
}

function addGroupedKey(map: Map<string, string[]>, key: string | null, id: string) {
  if (!key) return;
  const group = map.get(key) ?? [];
  group.push(id);
  map.set(key, group);
}

export function normalizeWeidianLinkKey(link: unknown): string | null {
  const value = String(link ?? '').trim();
  if (!value || !/weidian\.com/i.test(value)) return null;

  try {
    const url = new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`);
    const itemId = url.searchParams.get('itemID') ?? url.searchParams.get('itemId');
    if (itemId) return `weidian:itemid:${itemId.trim().toLowerCase()}`;
    return `${url.hostname}${url.pathname}`.toLowerCase().replace(/\/$/, '');
  } catch {
    return value.toLowerCase();
  }
}

export function analyzeCatalog(products: OperationsProduct[]) {
  const nameGroups = new Map<string, string[]>();
  const linkGroups = new Map<string, string[]>();
  const orderGroups = new Map<string, string[]>();

  for (const product of products) {
    addGroupedKey(nameGroups, normalizedText(product.name) || null, product.id);
    addGroupedKey(linkGroups, normalizeWeidianLinkKey(product.link), product.id);
    const orderKey = typeof product.order === 'number' ? String(product.order) : null;
    addGroupedKey(orderGroups, orderKey, product.id);
  }

  const duplicateIds = (groups: Map<string, string[]>) => new Set(
    [...groups.values()].filter((ids) => ids.length > 1).flat(),
  );
  const duplicateNames = duplicateIds(nameGroups);
  const duplicateLinks = duplicateIds(linkGroups);
  const duplicateOrders = duplicateIds(orderGroups);

  const rows: OperationsRow[] = products.map((product) => {
    const issues: OperationsIssue[] = [];
    if (!normalizedText(product.name)) issues.push('missing_name');
    if (!normalizedText(product.brand)) issues.push('missing_brand');
    if (!normalizedText(product.link)) issues.push('missing_link');
    if (product.price == null || product.price === '') issues.push('missing_price');
    if (!normalizedText(product.category)) issues.push('missing_category');
    if (!normalizedText(product.image)) issues.push('missing_image');

    const price = product.price == null || product.price === '' ? null : Number(product.price);
    if (price != null && (!Number.isFinite(price) || price < 0)) issues.push('invalid_price');
    if (duplicateNames.has(product.id)) issues.push('duplicate_name');
    if (duplicateLinks.has(product.id)) issues.push('duplicate_link');
    if (duplicateOrders.has(product.id)) issues.push('duplicate_order');

    const isWeidian = normalizeWeidianLinkKey(product.link) !== null;
    const hasMissingAffiliateLinks = AFFILIATE_KEYS.some((key) => !normalizedText(product[key]));
    if (isWeidian && hasMissingAffiliateLinks) issues.push('missing_affiliates');

    return { ...product, issues };
  });

  const counts = Object.fromEntries(
    ISSUE_TYPES.map((issue) => [issue, rows.filter((row) => row.issues.includes(issue)).length]),
  ) as Record<OperationsIssue, number>;

  return { total: rows.length, counts, rows };
}

export function filterOperationsRows(rows: OperationsRow[], filters: OperationsFilters) {
  const query = normalizedText(filters.query);
  const brand = normalizedText(filters.brand);
  const category = normalizedText(filters.category);

  return rows.filter((row) => {
    if (query) {
      const haystack = normalizedText(`${row.id} ${row.name ?? ''} ${row.brand ?? ''} ${row.category ?? ''} ${row.link ?? ''}`);
      if (!haystack.includes(query)) return false;
    }
    if (brand && normalizedText(row.brand) !== brand) return false;
    if (category && normalizedText(row.category) !== category) return false;

    const price = row.price == null || row.price === '' ? null : Number(row.price);
    if (filters.minPrice != null && (price == null || !Number.isFinite(price) || price < filters.minPrice)) return false;
    if (filters.maxPrice != null && (price == null || !Number.isFinite(price) || price > filters.maxPrice)) return false;
    if (filters.issue && filters.issue !== 'all' && !row.issues.includes(filters.issue)) return false;
    return true;
  });
}

const EMPTY_EXPORT_PLACEHOLDER = 'null';

function rawExportRows(rows: Array<OperationsProduct | OperationsRow>) {
  return rows.map((row) => {
    const { issues: _issues, ...raw } = row as OperationsRow;
    return Object.fromEntries(
      Object.entries(raw).filter(([key]) => !OMITTED_EXPORT_KEYS.has(key)),
    );
  });
}

function isEmptyExportValue(value: unknown) {
  if (value == null) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  return false;
}

function exportHeaders(rawRows: Array<Record<string, unknown>>) {
  const discovered = new Set(
    rawRows.flatMap((row) => Object.keys(row)).filter((key) => !OMITTED_EXPORT_KEYS.has(key)),
  );
  return [
    ...PREFERRED_EXPORT_KEYS.filter((key) => discovered.has(key)),
    ...[...discovered].filter((key) => !PREFERRED_EXPORT_KEYS.includes(key)).sort(),
  ];
}

/** Align every row to the same keys; blanks become JSON null / CSV "null". */
function normalizedExportRows(rows: Array<OperationsProduct | OperationsRow>) {
  const rawRows = rawExportRows(rows);
  const headers = exportHeaders(rawRows);
  return {
    headers,
    rows: rawRows.map((row) => {
      const normalized: Record<string, unknown> = {};
      for (const header of headers) {
        const value = row[header];
        normalized[header] = isEmptyExportValue(value) ? null : value;
      }
      return normalized;
    }),
  };
}

export function serializeOperationsJson(rows: Array<OperationsProduct | OperationsRow>) {
  return JSON.stringify(normalizedExportRows(rows).rows, null, 2);
}

function spreadsheetSafe(value: string) {
  return /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
}

function csvCell(value: unknown) {
  const text = isEmptyExportValue(value)
    ? EMPTY_EXPORT_PLACEHOLDER
    : typeof value === 'object'
      ? JSON.stringify(value)
      : String(value);
  return `"${spreadsheetSafe(text).replace(/"/g, '""')}"`;
}

export function serializeOperationsCsv(rows: Array<OperationsProduct | OperationsRow>) {
  const { headers, rows: normalizedRows } = normalizedExportRows(rows);
  return [
    headers.join(','),
    ...normalizedRows.map((row) => headers.map((header) => csvCell(row[header])).join(',')),
  ].join('\r\n');
}

function buildOperationsWorkbook(rows: Array<OperationsProduct | OperationsRow>) {
  const { headers, rows: normalizedRows } = normalizedExportRows(rows);
  const worksheet = XLSX.utils.json_to_sheet(normalizedRows, { header: headers });
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Catalog');
  return workbook;
}

export function downloadOperationsExport(
  rows: Array<OperationsProduct | OperationsRow>,
  format: 'json' | 'csv' | 'xlsx',
) {
  const date = new Date().toISOString().replace(/[:.]/g, '-');
  if (format === 'xlsx') {
    XLSX.writeFileXLSX(buildOperationsWorkbook(rows), `ali-catalog-${date}.xlsx`);
    return;
  }

  const body = format === 'json' ? serializeOperationsJson(rows) : serializeOperationsCsv(rows);
  const type = format === 'json' ? 'application/json' : 'text/csv;charset=utf-8';
  const url = URL.createObjectURL(new Blob([body], { type }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `ali-catalog-${date}.${format}`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
